import { useEffect, useRef } from 'react';
import { useQueryClient, type Query } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import type { LookupType } from '@/models/lookups';
import { useGlobalSelector } from './useGlobalState';

/**
 * Data that does not depend on the working year, kept on a switch: the academic years
 * themselves, the school list, global settings and key formats, and the lookups (except
 * {@link YEAR_SCOPED_LOOKUPS}). Dropping them would unmount the shell's school picker and
 * settings-driven layout, not just the page.
 */
const yearIndependentKeys: readonly (readonly unknown[])[] = [
  queryKeys.academics.years,
  queryKeys.school.list,
  queryKeys.globalSettings.all,
  queryKeys.lookups.all,
];

/** Lookup types the API resolves in the request's year (`['lookups', type, ...]`). */
const YEAR_SCOPED_LOOKUPS: ReadonlySet<unknown> = new Set<LookupType>(['periods']);

/** The query is under one of {@link yearIndependentKeys}, and is not a year-scoped lookup. */
export const isYearIndependentQuery = (query: Pick<Query, 'queryKey'>): boolean =>
  !(query.queryKey[0] === queryKeys.lookups.all[0] && YEAR_SCOPED_LOOKUPS.has(query.queryKey[1])) &&
  yearIndependentKeys.some((prefix) => prefix.every((part, index) => query.queryKey[index] === part));

/**
 * Resets the app's cached data when the user switches the working academic year. Every
 * request carries the year (`X-Academic-Year-Id`), so cached results belong to the
 * old one: they are dropped (not just marked stale), so no page shows the old year's
 * rows while the new year loads, and active queries refetch. Only a switch from one
 * year to another counts: the first year set (from none) and the reset to none on a
 * school switch do not reset anything. Data that does not depend on the year (see
 * {@link isYearIndependentQuery}) is kept. Year-scoped pages remount on a switch (they are
 * keyed by the year in `AcademicYearRequired`), so their local state starts over in the new year.
 *
 * Mount once, in the layout that hosts the year switcher.
 */
export function useRefetchOnAcademicYearChange(): void {
  const queryClient = useQueryClient();
  const yearId = useGlobalSelector((s) => s.activeAcademicYearId);
  const previousRef = useRef(yearId);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = yearId;
    if (previous == null || yearId == null || previous === yearId) return;
    void queryClient.resetQueries({ predicate: (query) => !isYearIndependentQuery(query) });
  }, [yearId, queryClient]);
}
